import { MultiSelectTalent } from '../type';

export interface TalentFavorite {
    _id: string;
    type: string;
    upercutId?: string;
    createdAt: string;
    metadata?: {
        notes?: string;
        tags?: string[];
        priority?: number;
        campaign?: string;
    };
}

export interface TalentFavoriteData {
    talent: {
        _id: string;
        upercutFavorites?: TalentFavorite[];
    };
}

export type TalentFavoritesMap = Record<string, TalentFavorite[]>;

/**
 * Helper function to build talent favorites map from selected talents and favorites data.
 * This standardizes the manual map building pattern used across multiple components.
 * 
 * @param selectedTalents Array of selected talent objects with IDs
 * @param favorites Array of favorite data containing talent and upercutFavorites
 * @returns Object mapping talent IDs to their favorite arrays
 */
export const buildTalentFavoritesMap = (
    selectedTalents: MultiSelectTalent[],
    favorites: TalentFavoriteData[]
): TalentFavoritesMap => {
    return selectedTalents.reduce((map: TalentFavoritesMap, talent) => {
        const favorite = favorites.find(f => f.talent._id === talent._id);
        if (favorite?.talent.upercutFavorites) {
            map[talent._id] = favorite.talent.upercutFavorites;
        }
        return map;
    }, {});
};

/**
 * Alternative version that works with talent objects containing upercutFavorites directly.
 * Useful when working with talent data that already includes favorites.
 * 
 * @param selectedTalents Array of selected talent objects with IDs
 * @param talentsWithFavorites Array of talent objects containing upercutFavorites
 * @returns Object mapping talent IDs to their favorite arrays
 */
export const buildTalentFavoritesMapDirect = (
    selectedTalents: MultiSelectTalent[],
    talentsWithFavorites: Array<{ _id: string; upercutFavorites?: TalentFavorite[] }>
): TalentFavoritesMap => {
    return selectedTalents.reduce((map: TalentFavoritesMap, talent) => {
        const talentData = talentsWithFavorites.find(t => t._id === talent._id);
        if (talentData?.upercutFavorites) {
            map[talent._id] = talentData.upercutFavorites;
        }
        return map;
    }, {});
};
